import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Home, BookOpen, MessageCircle, Camera, FileText, Sparkles, LogOut, GraduationCap,
  CalendarCheck, Target, Library, Sun, Menu, Settings, X,
} from "lucide-react";

const NAV = [
  { to: "/panel", icon: Home, label: "Panel", testid: "nav-panel" },
  { to: "/hedef", icon: Target, label: "Hedef", testid: "nav-hedef" },
  { to: "/bugun", icon: CalendarCheck, label: "Bugün", testid: "nav-bugun" },
  { to: "/dersler", icon: BookOpen, label: "Dersler", testid: "nav-dersler" },
  { to: "/ogretmen", icon: MessageCircle, label: "Öğretmen", testid: "nav-ogretmen" },
  { to: "/cozum-analiz", icon: Camera, label: "Çözüm", testid: "nav-cozum" },
  { to: "/denemeler", icon: FileText, label: "Deneme", testid: "nav-deneme" },
  { to: "/koc", icon: Sparkles, label: "Koç", testid: "nav-koc" },
];
const MORE_NAV = [
  { to: "/yaz-kampi", icon: Sun, label: "Yaz Kampı", testid: "nav-yaz" },
  { to: "/kaynaklar", icon: Library, label: "Kaynaklar", testid: "nav-kaynak" },
  { to: "/ayarlar", icon: Settings, label: "Ayarlar", testid: "nav-ayarlar" },
];

export default function StudentLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const isActive = (to) => loc.pathname === to || (to !== "/panel" && loc.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-0">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/panel" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-8 h-8 rounded-lg bg-[#FF6B6B] flex items-center justify-center text-white">
              <GraduationCap className="w-4 h-4" />
            </div>
            <span className="font-heading font-bold text-slate-900 hidden sm:inline">Ela AI Akademi</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={n.testid}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
                  isActive(n.to)
                    ? "bg-rose-50 text-[#FF6B6B]"
                    : "text-slate-600 hover:bg-slate-100"
                }`}>
                <n.icon className="w-4 h-4" />
                {n.label}
              </Link>
            ))}
            <div className="relative">
              <button onClick={() => setMoreOpen(!moreOpen)} data-testid="more-nav-btn"
                className="px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 text-slate-600 hover:bg-slate-100">
                <Menu className="w-4 h-4" />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[160px] z-50">
                  {MORE_NAV.map((n) => (
                    <Link key={n.to} to={n.to} onClick={() => setMoreOpen(false)}
                      data-testid={n.testid}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                      <n.icon className="w-4 h-4" /> {n.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
              <span>{user?.avatar_emoji}</span>
              <span className="font-medium">{user?.full_name}</span>
            </div>
            <Link to="/ayarlar" data-testid="settings-link"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 hidden sm:inline-flex">
              <Settings className="w-4 h-4" />
            </Link>
            <button onClick={logout} data-testid="logout-btn"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-slate-200">
        <div className="grid grid-cols-8">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`m-${n.testid}`}
              className={`flex flex-col items-center gap-0.5 py-2 text-[8px] font-medium ${
                isActive(n.to) ? "text-[#FF6B6B]" : "text-slate-500"
              }`}>
              <n.icon className="w-4 h-4" />
              <span className="leading-tight">{n.label}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Mobile floating "more" button */}
      <div className="md:hidden fixed bottom-20 right-3 z-40">
        <button onClick={() => setMoreOpen(!moreOpen)} data-testid="m-more-btn"
          className="w-11 h-11 rounded-full bg-slate-900 text-white shadow-lg flex items-center justify-center">
          {moreOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        {moreOpen && (
          <div className="absolute bottom-14 right-0 bg-white border border-slate-200 rounded-2xl shadow-xl py-1 min-w-[170px]">
            {MORE_NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMoreOpen(false)}
                data-testid={`m-${n.testid}`}
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                <n.icon className="w-4 h-4" /> {n.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
